import React from 'react';
import PropTypes from 'prop-types';
import { Box, Drawer, IconButton, Tab, Tabs, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/** The right-hand panel every editor opens in: a header, optional tabs, scrolling body, sticky footer. */
const EditorDrawer = ({ open, onClose, title, subtitle, media, actions, tabs, tab, onTabChange, footer, children }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    // The app theme puts drawers below the app bar; this one opens over everything like a dialog.
    sx={{ zIndex: (theme) => theme.zIndex.modal }}
    PaperProps={{ sx: { width: { xs: '100vw', sm: 560 }, display: 'flex', flexDirection: 'column' } }}
  >
    <Box sx={{ px: 3, pt: 2.5, pb: tabs ? 0 : 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {media}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {actions}
          <IconButton aria-label="Close" onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      {tabs && (
        <Tabs
          value={tab}
          onChange={(event, value) => onTabChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mt: 1.5, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 500 } }}
        >
          {tabs.map((entry) => (
            <Tab key={entry.value} value={entry.value} label={entry.label} />
          ))}
        </Tabs>
      )}
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>{children}</Box>
    {footer && (
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          bgcolor: 'background.paper',
        }}
      >
        {footer}
      </Box>
    )}
  </Drawer>
);

EditorDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  media: PropTypes.node,
  actions: PropTypes.node,
  tabs: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.node })),
  tab: PropTypes.string,
  onTabChange: PropTypes.func,
  footer: PropTypes.node,
  children: PropTypes.node,
};

export default EditorDrawer;
